// Centralized Logging Service for Production
class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.logLevel = process.env.REACT_APP_LOG_LEVEL || 'info';
    this.levels = {
      error: 0,
      warn: 1,
      info: 2,
      debug: 3
    };
  }

  shouldLog(level) {
    return this.levels[level] <= this.levels[this.logLevel];
  }

  formatMessage(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    
    if (data) {
      return `${prefix} ${message}`;
    }
    return `${prefix} ${message}`;
  }

  error(message, data = null) {
    if (this.shouldLog('error')) {
      this.saveToLocalStorage('error', message, data);
    }
  }

  warn(message, data = null) {
    if (this.shouldLog('warn')) {
      this.saveToLocalStorage('warn', message, data);
    }
  }

  info(message, data = null) {
    if (this.shouldLog('info')) {
      this.saveToLocalStorage('info', message, data);
    }
  }

  debug(message, data = null) {
    if (this.shouldLog('debug')) {
      this.saveToLocalStorage('debug', message, data);
    }
  }

  // Save logs to localStorage and send to backend for file logging
  async saveToLocalStorage(level, message, data = null) {
    try {
      const logEntry = {
        level,
        message,
        data,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };
      
      // Save to localStorage
      const logs = JSON.parse(localStorage.getItem('radiostreamin-logs') || '[]');
      logs.push(logEntry);
      
      // Keep only last 100 logs
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100);
      }
      
      localStorage.setItem('radiostreamin-logs', JSON.stringify(logs));
      
      // Also send to backend for file logging (in development)
      if (this.isDevelopment && level === 'error') {
        this.sendLogToBackend(logEntry);
      }
    } catch (error) {
      // Silent fail - don't log logging errors
    }
  }

  // Send log to backend for file storage
  async sendLogToBackend(logEntry) {
    try {
      await fetch('/api/analytics/frontend-log/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(logEntry)
      });
    } catch (error) {
      // Silent fail - don't log logging errors
    }
  }

  // User action logging
  logUserAction(action, userId, data = {}) {
    this.saveToLocalStorage('info', `User Action: ${action}`, { userId, ...data });
  }

  // Audio event logging
  logAudioEvent(event, stationName, data = {}) {
    this.saveToLocalStorage('info', `Audio Event: ${event}`, { stationName, ...data });
  }

  // Performance logging
  logPerformance(operation, duration, data = {}) {
    this.saveToLocalStorage('debug', `Performance: ${operation}`, { duration, ...data });
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;
